/**
 * PublishButton - Publish / Update / Unpublish workspace snapshots (RFC-0295).
 *
 * Renders a button in the workspace toolbar. On click, opens a popover
 * showing publication status with Publish / Update / Unpublish actions.
 * Each publish creates a version with its own unique share URL.
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocaleFormat } from '../../i18n/useLocaleFormat'
import { Share2, Copy, Check, Loader2, RotateCw, EyeOff } from 'lucide-react'
import { usePublicationStatus, usePublishProject, useUnpublishProject } from '../../api/hooks'
import { ApiRequestError, listCanvases, type PublicationVersionInfo } from '../../api/client'
import { DEFAULT_CANVAS_ID } from '../../contexts/ActiveCanvasIdContext'
import Modal from '../ui/Modal'
import { isApiRecord } from '../../api/responseDecoders'
import { buildPublicShareUrl } from '../../utils/shareUrl'
import { trackPublishResult } from '../../analytics/chatHeaderAnalytics'

/** Pull the localized `detail` string out of an API error body, if present. */
function readErrorDetail(err: ApiRequestError): string | null {
  if (!isApiRecord(err.errorData)) return null
  const detail = err.errorData.detail
  return typeof detail === 'string' && detail ? detail : null
}

interface PublishButtonProps {
  projectId: string
  /** Called when the trigger opens the popover (not when it closes it). */
  onOpen?: () => void
}

export function PublishButton({ projectId, onOpen }: PublishButtonProps) {
  const [open, setOpen] = useState(false)
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null)
  // Non-null means the exclusion confirmation is open. `titles` comes from the
  // client-side preflight when it can see the canvases; `message` is the
  // server's own localized refusal, used when it cannot (see below).
  const [pendingExclusion, setPendingExclusion] = useState<{
    titles: string[]
    message: string | null
  } | null>(null)
  // The mutation's isPending does NOT cover the preflight listCanvases call, so
  // without this a second click starts a second preflight and both can reach
  // runPublish, creating two publications.
  const [preflighting, setPreflighting] = useState(false)
  const [publishError, setPublishError] = useState<string | null>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  const { data: status, isLoading: statusLoading } = usePublicationStatus(projectId)
  const publishMutation = usePublishProject()
  const unpublishMutation = useUnpublishProject()
  const { t } = useTranslation('workspace')
  const { formatDate } = useLocaleFormat()

  // Close popover on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      const target = e.target
      if (target instanceof Node && popoverRef.current && !popoverRef.current.contains(target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // RFC-1577 `project_publish_result`: the publication state at the time of the
  // action — first publish vs a new version, and how many versions existed.
  const isPublished = status?.is_published === true
  const versionCount = status ? status.versions.length : undefined

  const runPublish = useCallback(
    async (acknowledgeExcludedCanvases: boolean) => {
      const action = isPublished ? 'new_version' : 'publish'
      try {
        await publishMutation.mutateAsync({ projectId, acknowledgeExcludedCanvases })
        trackPublishResult({ projectId, action, versionCount })
        setPendingExclusion(null)
        setPublishError(null)
      } catch (err) {
        // The server is the authority on which canvases exist, and it does not
        // apply the multi-canvas entitlement filter that `listCanvases` does.
        // So the preflight below can legitimately see one canvas while the
        // server sees several — e.g. when the gate is off for this user, which
        // is every user on an environment where the gate row is not deployed.
        // Without handling the refusal here, that user could never publish and
        // would never be told why.
        if (err instanceof ApiRequestError && err.status === 409) {
          setPendingExclusion({ titles: [], message: readErrorDetail(err) })
          return
        }
        // Everything else — including the 503 the server returns when it cannot
        // determine the publish scope — has to reach the user. Logging alone
        // leaves a button that silently does nothing.
        trackPublishResult({ projectId, action, versionCount, error: err instanceof Error ? err : null })
        setPendingExclusion(null)
        setOpen(false)
        setPublishError(
          (err instanceof ApiRequestError ? readErrorDetail(err) : null) ??
            t('publishButton.publishFailed'),
        )
        console.error('Failed to publish project:', err)
      }
    },
    [isPublished, projectId, publishMutation, t, versionCount],
  )

  const handlePublish = useCallback(async () => {
    // The mutation's isPending does not cover the preflight below, so guard
    // synchronously: two quick clicks would otherwise run two preflights and
    // both reach runPublish, creating two publications.
    if (preflighting || publishMutation.isPending) return
    setPreflighting(true)
    setPublishError(null)
    try {
      // Publishing captures only the main canvas, and the published viewer has
      // no way to show any other, so a multi-canvas project must not be
      // published without the user seeing what is left out. The API enforces
      // this too — this is the readable half, and it can show a per-canvas
      // list because it has the titles.
      try {
        const { canvases } = await listCanvases(projectId)
        const secondary = canvases
          .filter((canvas) => canvas.id !== DEFAULT_CANVAS_ID)
          .map((canvas) => canvas.title || canvas.id)
        if (secondary.length > 0) {
          setPendingExclusion({ titles: secondary, message: null })
          return
        }
      } catch (err) {
        // This listing is only an optimisation. The server does its own,
        // unfiltered check and will refuse (409) or report that it could not
        // determine the scope (503) — both handled in runPublish.
        console.error('Could not check canvases before publishing:', err)
      }
      await runPublish(false)
    } finally {
      setPreflighting(false)
    }
  }, [preflighting, projectId, publishMutation.isPending, runPublish])

  const handleUnpublish = useCallback(async () => {
    try {
      await unpublishMutation.mutateAsync(projectId)
      trackPublishResult({ projectId, action: 'unpublish', versionCount })
    } catch (err) {
      trackPublishResult({ projectId, action: 'unpublish', versionCount, error: err instanceof Error ? err : null })
      console.error('Failed to unpublish project:', err)
    }
  }, [projectId, unpublishMutation, versionCount])

  const handleCopyVersion = useCallback((version: PublicationVersionInfo) => {
    // Post 1591255772631720: NOT window.location.origin. This link is copied
    // out and distributed, so it must use the canonical public host rather
    // than whichever alias the publisher happened to be on.
    const fullUrl = buildPublicShareUrl(version.share_url)
    navigator.clipboard.writeText(fullUrl).then(() => {
      trackPublishResult({ projectId, action: 'copy_link', versionCount })
      setCopiedSlug(version.version_slug)
      setTimeout(() => setCopiedSlug(null), 2000)
    })
  }, [projectId, versionCount])

  const isPublishing = publishMutation.isPending
  const isUnpublishing = unpublishMutation.isPending
  const isBusy = isPublishing || isUnpublishing || preflighting

  return (
    <div className="relative" ref={popoverRef}>
      <button
        onClick={() => {
          if (!open) onOpen?.()
          setOpen(!open)
        }}
        // Matches AppHeader's `iconButton`; this trigger sits in that row.
        className="flex h-9 w-9 items-center justify-center rounded-lg text-chrome/70 hover:bg-chrome/10 hover:text-chrome transition-colors cursor-pointer"
        title={t('publishButton.publishProject')}
        data-testid="publish-button"
      >
        <Share2 className="w-4 h-4" />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-lg shadow-xl z-50 overflow-hidden"
          data-testid="publish-popover"
        >
          <div className="p-3 border-b border-border">
            <h3 className="text-sm font-medium text-white">{t('publishButton.publishProjectHeading')}</h3>
            <p className="text-xs font-medium text-muted-foreground mt-1">
              {t('publishButton.publishDescription')}
            </p>
          </div>

          <div className="p-3">
            {statusLoading ? (
              <div className="flex justify-center py-2">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            ) : status?.is_published ? (
              /* Published state */
              <div className="space-y-3">
                {/* Version history */}
                {status.versions.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground">{t('publishButton.publishedVersions')}</p>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {status.versions.map((v) => (
                        <div
                          key={v.version_slug}
                          className="flex items-center gap-2 px-2 py-1.5 rounded bg-surface-light/50"
                          data-testid={`version-row-${v.version_number}`}
                        >
                          <span className="text-xs font-medium text-white/70 flex-1 truncate">
                            v{v.version_number}
                            <span className="text-white/40 ml-1.5">
                              {formatDate(v.created_at)}
                            </span>
                          </span>
                          <button
                            onClick={() => handleCopyVersion(v)}
                            className="p-1 rounded hover:bg-white/10 text-muted-foreground hover:text-white transition-colors shrink-0"
                            title={t('publishButton.copyLink', { version: v.version_number })}
                            data-testid={`copy-version-${v.version_number}`}
                          >
                            {copiedSlug === v.version_slug ? (
                              <Check className="w-3 h-3 text-green-400" />
                            ) : (
                              <Copy className="w-3 h-3" />
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={handlePublish}
                    disabled={isBusy}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-primary/20 hover:bg-primary/30 text-primary rounded-md transition-colors text-sm disabled:opacity-50"
                    data-testid="update-publication"
                  >
                    {isPublishing ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <RotateCw className="w-3.5 h-3.5" />
                    )}
                    {t('publishButton.newVersion')}
                  </button>
                  <button
                    onClick={handleUnpublish}
                    disabled={isBusy}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-md transition-colors text-sm disabled:opacity-50"
                    data-testid="unpublish-button"
                  >
                    {isUnpublishing ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <EyeOff className="w-3.5 h-3.5" />
                    )}
                    {t('publishButton.unpublish')}
                  </button>
                </div>
              </div>
            ) : (
              /* Not published state */
              <button
                onClick={handlePublish}
                disabled={isBusy}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-primary/20 hover:bg-primary/30 text-primary rounded-md transition-colors text-sm disabled:opacity-50"
                data-testid="publish-project-button"
              >
                {isPublishing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Share2 className="w-4 h-4" />
                )}
                {t('publishButton.publish')}
              </button>
            )}
          </div>
        </div>
      )}

      {publishError && (
        <div
          // Above the z-50 popover, not below it: at z-40 this sat behind the
          // popover and could hide the only failure feedback rendered.
          className="absolute right-0 top-full z-[60] mt-2 w-80 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          role="alert"
          data-testid="publish-error"
        >
          {publishError}
        </div>
      )}

      <Modal
        isOpen={pendingExclusion !== null}
        onClose={() => setPendingExclusion(null)}
        title={t('publishButton.excludedCanvases.title')}
        size="sm"
      >
        <div data-testid="publish-excluded-canvases-dialog">
          <p className="text-sm text-neutral-300">
            {pendingExclusion?.message ?? t('publishButton.excludedCanvases.body')}
          </p>
          {pendingExclusion?.titles.length ? (
            <ul
              className="mt-3 max-h-40 list-disc overflow-y-auto pl-5 text-sm text-neutral-400"
              data-testid="publish-excluded-canvases-list"
            >
              {pendingExclusion.titles.map((title) => (
                <li key={title}>{title}</li>
              ))}
            </ul>
          ) : null}
          <div className="mt-5 flex justify-end gap-2">
            <button
              onClick={() => setPendingExclusion(null)}
              className="rounded-md px-3 py-2 text-sm text-neutral-300 transition-colors hover:bg-white/5"
              data-testid="publish-excluded-canvases-cancel"
            >
              {t('publishButton.excludedCanvases.cancel')}
            </button>
            <button
              onClick={() => void runPublish(true)}
              disabled={isPublishing}
              className="flex items-center gap-2 rounded-md bg-primary/20 px-3 py-2 text-sm text-primary transition-colors hover:bg-primary/30 disabled:opacity-50"
              data-testid="publish-excluded-canvases-confirm"
            >
              {isPublishing && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {t('publishButton.excludedCanvases.confirm')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
